import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined"
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import * as fileDownload from "js-file-download"
import { useState } from "react"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { useIsMobile } from "../../../common/utils"
import DialogHeader from "../components/DialogHeader"
import { useDialogContainer } from "../utils"

const UPLOADED_LEADS_CSV_COLUMNS = [
  "Name",
  "Phone",
  "Email",
  "Office Phone",
  "Company",
  "Address",
  "City",
  "State",
  "Zip",
  "License Type",
  "License Number",
  "Years in Business",
  "Sales Volume",
  "Homes Sold",
  "Rating",
  "Website",
  "LinkedIn",
  "Facebook",
  "Instagram",
  "Notes",
]

export default function ImportUploadedLeadsDialog({
  open,
  onClose,
  onImportQueued,
}) {
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploadName, setUploadName] = useState("")
  const [isImporting, setIsImporting] = useState(false)
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false)
  const [errorText, setErrorText] = useState(null)

  const isMobile = useIsMobile()
  const Container = useDialogContainer()
  const axios = useAuthorizedAxiosClient()

  function resetForm() {
    setErrorText(null)
    setSelectedFile(null)
    setUploadName("")
  }

  function handleSoftClose() {
    if (!isImporting && !isDownloadingTemplate) {
      resetForm()
      onClose()
    }
  }

  function filenameWithoutCsv(filename) {
    return String(filename || "").replace(/\.csv$/i, "")
  }

  function handleFileChanged(e) {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0]
      setSelectedFile(file)
      setUploadName((current) =>
        current.trim() ? current : filenameWithoutCsv(file.name)
      )
      setErrorText(null)
    } else {
      setSelectedFile(null)
    }
    e.target.value = ""
  }

  function handleDownloadTemplateClicked() {
    setIsDownloadingTemplate(true)
    axios
      .get("/crm/api/uploaded-leads/export-csv-sample/", {
        responseType: "blob",
      })
      .then(({ data, headers }) => {
        const contentType = headers?.["content-type"] || ""
        if (!contentType.includes("text/csv")) {
          throw new Error("Unexpected response from template download.")
        }
        fileDownload(data, "uploaded-leads-import-template.csv")
      })
      .catch(async (err) => {
        let errorMsg = "Failed to download template."
        const responseData = err.response?.data
        if (responseData instanceof Blob) {
          try {
            const text = await responseData.text()
            const parsed = JSON.parse(text)
            errorMsg =
              parsed.detail ||
              parsed.file?.[0] ||
              parsed.non_field_errors?.[0] ||
              errorMsg
          } catch (parseError) {
            if (err.message) {
              errorMsg = err.message
            }
          }
        } else if (err.response?.data?.detail) {
          errorMsg = err.response.data.detail
        } else if (err.response?.data?.error) {
          errorMsg = err.response.data.error
        } else if (err.message) {
          errorMsg = err.message
        }
        setErrorText(`Error: ${errorMsg}`)
      })
      .finally(() => setIsDownloadingTemplate(false))
  }

  function handleImportButtonClicked() {
    if (!selectedFile) return

    setIsImporting(true)
    setErrorText(null)

    const formData = new FormData()
    formData.append("file", selectedFile)
    formData.append("upload_name", uploadName.trim())

    axios
      .post("/crm/api/uploaded-leads/import-csv/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(({ data }) => {
        onImportQueued(data)
        setIsImporting(false)
        resetForm()
        onClose()
      })
      .catch((err) => {
        let errorMsg = "An unexpected error occurred."
        const responseData = err.response?.data
        if (responseData) {
          if (typeof responseData === "string") {
            errorMsg = responseData
          } else if (responseData.detail) {
            errorMsg = responseData.detail
          } else if (responseData.file) {
            errorMsg = Array.isArray(responseData.file)
              ? responseData.file[0]
              : responseData.file
          } else if (responseData.error) {
            errorMsg = responseData.error
          }
        }
        setErrorText(`Error: ${errorMsg}`)
      })
      .finally(() => setIsImporting(false))
  }

  return (
    <Container
      open={open}
      onClose={handleSoftClose}
      anchor="right"
      fullScreen={isMobile}
    >
      <DialogHeader title="Import Leads" onClose={handleSoftClose} />
      <DialogContent>
        <Column sx={{ rowGap: 4, mt: 2 }}>
          <Alert severity="info">
            Upload a CSV with up to 5,000 leads per file. Required columns:{" "}
            <strong>Name</strong>, <strong>Phone</strong>. Supported columns:{" "}
            {UPLOADED_LEADS_CSV_COLUMNS.join(", ")}. Imports run in the
            background and you&apos;ll be notified when processing completes.
          </Alert>

          <Column sx={{ rowGap: 1 }}>
            <Typography variant="h6">Upload name</Typography>
            <Typography variant="body2" color="text.secondary">
              Assign a name to this import so you can filter Import Leads and
              save a view.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ex: Spring recruiting, Realty firm, etc.
            </Typography>
            <TextField
              size="small"
              label="Upload name"
              value={uploadName}
              onChange={(e) => setUploadName(e.target.value)}
              inputProps={{ maxLength: 256 }}
            />
          </Column>

          <Column sx={{ rowGap: 1 }}>
            <Typography variant="h6">Upload Lead File</Typography>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadOutlinedIcon />}
              sx={{ py: 2, borderStyle: "dashed", borderWidth: 2 }}
            >
              {selectedFile ? selectedFile.name : "Choose CSV File"}
              <input
                type="file"
                hidden
                accept=".csv, text/csv"
                onChange={handleFileChanged}
              />
            </Button>
          </Column>

          <Row sx={{ justifyContent: "center", alignItems: "center" }}>
            <Typography variant="body2" color="text.secondary">
              or
            </Typography>
          </Row>

          <Button
            variant="text"
            startIcon={<InsertDriveFileOutlinedIcon />}
            onClick={handleDownloadTemplateClicked}
            disabled={isDownloadingTemplate}
          >
            Download CSV template
          </Button>

          {errorText ? (
            <Alert severity="error" onClose={() => setErrorText(null)}>
              {errorText}
            </Alert>
          ) : null}
        </Column>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSoftClose} disabled={isImporting}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          loading={isImporting}
          disabled={!selectedFile}
          onClick={handleImportButtonClicked}
        >
          Import
        </LoadingButton>
      </DialogActions>
    </Container>
  )
}
